import type { TypeTableColumn } from '@thefarmersfront/kitchen';

import { BadgeCell, TextButtonCell } from '~/components/common';
import { ReceiveSupplierCell, ReceiveSupplierHeader } from '~/components/receive';

import { disableInE2E, formatNumber } from '~/utils/common';
import { formatDate } from '~/utils/date';

import { RECEIVE_TOOLTIP_MESSAGE } from '~/constants';

import { formatReceiveExpectedDateTime } from '../formatReceiveExpectedDateTime';
import { RECEIVE_REPORT_STATUS_COLOR_MAP, RECEIVE_STATUS_STATUS_COLOR_MAP } from './receiveBadgeColorMaps';

import type { TypeReceiveStatusRow } from '~/types/receive.types';

const countColumn = (
  accessorKey: 'skuCnt' | 'qty' | 'receivedSkuCnt' | 'receivedQty',
  header: string
): TypeTableColumn<TypeReceiveStatusRow> => ({
  accessorKey,
  header,
  size: 80,
  cell: ({ getValue }) => {
    const value = getValue<number | null>();
    return value ? formatNumber(value) : '-';
  },
  meta: { cellStyle: { textAlign: 'right' } },
});

/**
 * [입고관리 > 입고현황] 페이지 테이블
 */
const getReceiveStatusColumnDefs = ({
  mode = 'DEFAULT',
  onClickViewDetail,
}: {
  mode?: 'DEFAULT' | '3PL';
  onClickViewDetail: (data: TypeReceiveStatusRow) => void;
}) => {
  const reportStatusColumn: TypeTableColumn<TypeReceiveStatusRow> = {
    accessorKey: 'reportStatus',
    header: '리포트 상태',
    meta: { headerTooltip: { description: RECEIVE_TOOLTIP_MESSAGE.REPORT_STATUS_DESCRIPTION, maxWidth: '258px' } },
    size: 130,
    minSize: 130,
    cell: ({ getValue }) => {
      const value = getValue<TypeReceiveStatusRow['reportStatus']>();
      return value ? (
        <BadgeCell value={value} colorMap={RECEIVE_REPORT_STATUS_COLOR_MAP} variant="light" status />
      ) : (
        '-'
      );
    },
  };

  const columnDefs: TypeTableColumn<TypeReceiveStatusRow>[] = [
    {
      id: 'no',
      header: 'No',
      cell: ({ row }) => row.index + 1,
      size: 72,
      meta: { selection: true },
    },
    {
      accessorKey: 'requestCode',
      header: '입고요청번호',
      size: 180,
    },
    {
      accessorKey: 'status',
      header: '요청상태',
      size: 100,
      minSize: 100,
      cell: ({ getValue }) => {
        const value = getValue<TypeReceiveStatusRow['status']>();
        return value ? (
          <BadgeCell value={value} colorMap={RECEIVE_STATUS_STATUS_COLOR_MAP} variant="light" status />
        ) : (
          '-'
        );
      },
    },
    ...(mode === '3PL' ? [reportStatusColumn] : []),
    {
      id: 'estimatedArrivalDate',
      accessorFn: (data) => formatReceiveExpectedDateTime(data.estimatedArrivalDate, data.estimatedArrivalTime),
      header: '입고예정일시',
      size: 200,
    },
    {
      accessorKey: 'releaseCodeName',
      header: '입고 방법',
      size: 80,
    },
    {
      id: 'viewDetail',
      header: '',
      size: 80,
      cell: ({ row }) => (
        <TextButtonCell showDashIfNoValue={false} onClick={() => onClickViewDetail(row.original)}>
          상세보기
        </TextButtonCell>
      ),
    },
    {
      id: 'supplier',
      header: () => <ReceiveSupplierHeader />,
      cell: ({ row }) => <ReceiveSupplierCell data={row.original} />,
      minSize: 280,
      meta: { flex: disableInE2E(true) },
    },
    countColumn('skuCnt', '요청 품목'),
    countColumn('qty', '요청 수량'),
    countColumn('receivedSkuCnt', '입고 품목'),
    countColumn('receivedQty', '입고 수량'),
    {
      accessorKey: 'receivedDate',
      header: '입고확정일',
      size: 100,
      cell: ({ getValue }) => formatDate(getValue<string>(), 'YYYY.MM.DD') ?? '-',
    },
  ];

  return { columnDefs };
};

export default getReceiveStatusColumnDefs;
